/**
 * Навигационные гварды.
 *
 * ⚠️ ГЛАВНОЕ, ЧТО НАДО ЗНАТЬ ПРО ЭТОТ ФАЙЛ: **гвард — это UX-аффорданс, а не
 * авторизация.** Авторитет — Go-бэкенд: его RBAC fail-closed и вернёт 401/403
 * независимо от того, что решил роутер. Здесь мы всего лишь избавляем человека
 * от заведомо отказного запроса и от экрана сканирования, на котором каждый
 * разобранный QR молча падает.
 *
 * Ролевых проверок нет ни одной: `ResolveParticipantQr` разрешён `anyRole`,
 * так что и администратор, и наблюдатель одинаково пригодны для стойки.
 * Единственный вопрос, на который отвечает гвард, — «вошли или нет».
 */

import type { Router } from 'vue-router'

import { useAuthStore } from '@/stores/auth'

/** Хвост `document.title`. Совпадает с `<title>` в `index.html`. */
const APP_TITLE = 'Регистрация участников'

/**
 * Валидация `?redirect=` перед тем, как ей воспользоваться.
 *
 * Принимаем **только** строку, начинающуюся с одиночного `/`. Всё остальное —
 * `null`, то есть «редиректа не было».
 *
 * Что именно отсекается и почему:
 *   • `https://evil.com`  — абсолютный URL со схемой;
 *   • `//evil.com`        — protocol-relative: браузер читает это как хост,
 *                           а не как путь. Классический open-redirect в две строки;
 *   • `/\evil.com`        — браузеры нормализуют `\` в `/` в позиции authority,
 *                           так что это тот же `//evil.com` в маскировке;
 *   • не-строки           — `to.query.redirect` вполне может приехать массивом
 *                           (`?redirect=a&redirect=b`) или `null`.
 *
 * Экспортируется наружу: `LoginView` обязан гонять `route.query.redirect`
 * через эту же функцию, а не через свою копию проверки.
 */
export function safeRedirect(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  if (raw.length === 0 || raw[0] !== '/') return null
  // Второй символ `/` или `\` — попытка увести на чужой хост.
  if (raw[1] === '/' || raw[1] === '\\') return null
  return raw
}

/**
 * Вешает `beforeEach`/`afterEach` на переданный роутер.
 *
 * Отдельной функцией, а не побочным эффектом импорта: так порядок «создали
 * роутер → повесили гварды» виден в `index.ts`, а не зависит от порядка импортов.
 */
export function registerGuards(router: Router): void {
  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    /**
     * ★ КЛЮЧЕВАЯ СТРОКА ФАЙЛА.
     *
     * `bootstrap()` ждём **до любого решения о редиректе**. Access-токен живёт
     * только в памяти, поэтому на холодной загрузке — а её здесь провоцирует
     * всё подряд: телефон заблокировали, вкладку выгрузило из памяти, стойку
     * перезагрузили, потому что «подвисло» — `isAuthenticated === false` даже
     * при совершенно живой 30-дневной сессии. Редирект на `/login` в этот
     * момент означал бы форму входа при каждом пробуждении телефона.
     *
     * Ждать его на каждой навигации не дорого: в сторе он мемоизирован и после
     * первого вызова возвращает уже разрешённый промис.
     */
    try {
      await auth.bootstrap()
    } catch {
      /**
       * Восстановить сессию не удалось — Wi-Fi лёг, сервер отдал 500, refresh
       * протух. Это **не** повод вешать навигацию: продолжаем как аноним, и
       * ниже человек уедет на `/login`. Молчаливое падение гварда оставило бы
       * белый экран без единого следа в UI, что гораздо хуже.
       *
       * (`bootstrap()` по контракту не бросает; `try` стоит на случай, если
       *  это когда-нибудь перестанет быть правдой.)
       */
    }

    // ── 1. Публичный маршрут: только /login ─────────────────────────────
    if (to.meta.public) {
      if (auth.isAuthenticated && to.name === 'login') {
        // Уже вошли, а пришли на форму входа — уводим туда, куда собирались.
        const back = safeRedirect(to.query.redirect)
        // `back` на сам логин дал бы бесконечный цикл редиректов.
        if (back && router.resolve(back).name !== 'login') return back
        return { name: 'scan' }
      }
      return true
    }

    // ── 2. Приватный маршрут: нужен вход ────────────────────────────────
    if (!auth.isAuthenticated) {
      // `fullPath`, а не `path`: на случай, если у экрана сканирования
      // когда-нибудь появится состояние в query, терять его при перелогине
      // незачем.
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    return true
  })

  /**
   * Заголовок вкладки. В `afterEach`, а не в `beforeEach`, — заголовок должен
   * отражать то, куда мы **пришли**, а не то, куда собирались: отменённая или
   * перенаправленная навигация иначе оставила бы чужой заголовок.
   */
  router.afterEach((to) => {
    const title = to.meta.title
    document.title = title ? `${title} — ${APP_TITLE}` : APP_TITLE
  })
}
